<template>
  <layout class="pairing-page-container bg-transparent">
    <div class="w-full adm-container">
      <div class="bg-white rounded border border-grey-light flex p-4 sm:flex-col lg:flex-row">
        <div class="flex items-center filter-dropdown-container sm:w-full lg:w-4/5 sm:flex-col lg:flex-row">
          <div class="selection flex-1 flex w-full sm:flex-col lg:flex-col">
            <div class="title mb-2">Select Driver</div>
            <pt-select
              v-model="currentPairingDriver"
              :options="unpairedDrivers"
              :searchable="true"
              label-key="name"
              track-by="id"
              placeholder="Select Driver"
            />
          </div>
          <span class="mx-4 sm:hidden lg:block">
            <i class="fa fa-long-arrow-right" />
          </span>
          <span class="sm:block lg:hidden">
            <i class="fa fa-long-arrow-down" />
          </span>
          <div class="selection flex-1 flex w-full sm:mt-4 lg:mt-0 sm:flex-col lg:flex-col">
            <div class="title mb-2">Select Vehicle</div>
            <pt-select
              v-model="currentPairingVehicle"
              :searchable="true"
              :internal-search="false"
              :options="filteredUnpairedVehicle"
              placeholder="Select Vehicle"
              @search-change="searchInUnpairedVehicles"
            >
              <template
                slot="selectedOption"
                slot-scope="props">
                <span>{{ props.option.car_models }} {{ props.option.reg_number ? `- ${props.option.reg_number}` : '' }}</span>
              </template>
              <template
                slot="option"
                slot-scope="props">
                <span>{{ props.option.car_models }} {{ props.option.reg_number ? `- ${props.option.reg_number}` : '' }}</span>
              </template>
            </pt-select>
          </div>
        </div>
        <div class="flex items-end justify-end sm:w-full lg:w-1/5">
          <pt-button
            :disabled="!currentPairingDriver || !currentPairingVehicle"
            variant="primary"
            class="uppercase sm:mt-4 lg:mt-0 sm:w-full lg:w-32"
            @action="onPairBtnClicked(currentPairingDriver.id, currentPairingVehicle.id)"
          >pair
          </pt-button>
        </div>
      </div>

      <div class="w-full flex flex-col text-grey-darker pt-8">
        <div class="flex flex-1 sm:flex-col lg:flex-row">
          <div class="filter-dropdown-container flex mr-4 sm:w-full lg:w-1/3 sm:mb-2 lg:mb-0">
            <pt-dropdown
              :right="false"
              :text="selectedSearchDropDownText"
              class="w-full">
              <pt-dropdown-item @dd-item-clicked="setVehicleWithDriverSelector(true, true)">All</pt-dropdown-item>
              <pt-dropdown-item @dd-item-clicked="setVehicleWithDriverSelector(true, false)">With Vehicle</pt-dropdown-item>
              <pt-dropdown-item @dd-item-clicked="setVehicleWithDriverSelector(false, true)">Without Vehicle</pt-dropdown-item>
            </pt-dropdown>
          </div>
          <div class="search-input md:w-full lg:w-1/3">
            <pt-form-input
              v-model="pairingSearchKey"
              type="text"
              icon-class="fa-search"
              icon-position="left"
              placeholder="Search by driver or vehicle"
              class="mb-4 w-full"
              @input="debouncedSearchDriver"
            />
          </div>
          <div class="flex flex-1 items-center justify-end">
            <download-csv
              :download-url="exportUrl"
              file-name="Driver - Vehicles List"
              class="font-bold mb-4"
            />
          </div>
        </div>

        <pt-list v-if="!isLoadingDrivers">
          <div class="p-4 bg-transparent font-bold list-header border-b sm:hidden lg:flex">
            <div class="w-2/3 flex">
              <div class="w-1/3">Driver</div>
              <div class="w-1/3">Mobile</div>
              <div class="w-1/3">Vehicle</div>
            </div>
          </div>
          <div class="table-loader-container relative">
            <pt-list-item
              v-for="(pair, pairIndex) in driverList"
              :key="pairIndex"
            >
              <div class="flex flex-1 sm:flex-col lg:flex-row">
                <div class="flex items-center sm:flex-col lg:flex-row sm:w-full lg:w-2/3">
                  <div class="sm:w-full lg:w-1/3">
                    <span class="font-bold sm:mr-4 lg:mr-0 lg:hidden">Driver: </span>
                    <span>{{ pair.driver_name }}</span>
                  </div>
                  <div class="sm:w-full lg:w-1/3">
                    <span class="font-bold sm:mr-4 lg:mr-0 lg:hidden">Mobile: </span>
                    <span>{{ pair.number }}</span>
                  </div>
                  <div class="sm:w-full lg:w-1/3">
                    <span class="font-bold sm:mr-2 lg:mr-0 lg:hidden">Vehicle: </span>
                    <span>{{ pair.vehicle_company }} {{ pair.vehicle_models }} {{ pair.vehicle_reg_number ? `- ${pair.vehicle_reg_number}` : '' }}</span>
                  </div>
                </div>
                <div class="flex justify-end sm:w-full lg:w-1/3">
                  <pt-button
                    v-if="pair.driver_id && pair.vehicle_id"
                    variant="secondary"
                    class="uppercase pull-right sm:w-full lg:w-32 sm:mt-4 lg:mt-0"
                    @action="onUnpairBtnClicked(pair.driver_id, pair.vehicle_id)"
                  >unpair
                  </pt-button>
                </div>
              </div>
            </pt-list-item>
          </div>
        </pt-list>
        <table-placeholder
          :is-syncing="isLoadingDrivers"
          :data-length="driverList.length"
          loader-class="text-black"
        />
        <pt-pagination
          :total-rows="totalRows"
          :current-page="currentPage"
          :page-size="pageSize"
          class="mt-8"
          @page-changed="onPageChanged"
        />
      </div>
      <pt-modal
        ref="unpairPrompt"
        size="medium"
      >
        <div slot="modal-body">
          Do you want to unpair this driver?
        </div>
        <div
          slot="modal-footer"
          class="flex justify-end"
        >
          <pt-button
            variant="secondary"
            class="mr-2"
            @action="shouldNotUnPair"
          >
            No
          </pt-button>
          <pt-button
            variant="primary"
            @action="shouldUnPair">
            Yes
          </pt-button>
        </div>
      </pt-modal>
      <pt-modal
        ref="pairPrompt"
        size="medium"
      >
        <div slot="modal-body">
          Do you want to pair this driver?
        </div>
        <div
          slot="modal-footer"
          class="flex justify-end"
        >
          <pt-button
            variant="secondary"
            class="mr-2"
            @action="shouldNotPair"
          >
            No
          </pt-button>
          <pt-button
            variant="primary"
            @action="shouldPair">
            Yes
          </pt-button>
        </div>
      </pt-modal>
    </div>
  </layout>
</template>

<script>
import cloneArray from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { PtFormInput } from '@pathao/ui-components/esm/FormComponents';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { PtSelect } from '@pathao/ui-components/esm/Select';
import { PtModal } from '@pathao/ui-components/esm/Modal';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtDropdown, PtDropdownItem } from '@pathao/ui-components/esm/Dropdown';
import Config from '@config/';
import PaginationConfig from '@config/pagination';
import DownloadCsv from '@components/custom/DownloadCsv';
import { PtList, PtListItem } from '@components/custom/List';
import TablePlaceholder from '@components/custom/TablePlaceholder';
import Layout from '@layouts/main';
import makeRequest from '@utils/apiRequest';

export default {
  name: 'Pairing',
  page: {
    title: 'Pairing',
  },
  components: { PtButton, PtFormInput, PtList, PtListItem, Layout, PtSelect, PtModal, DownloadCsv, PtPagination, TablePlaceholder, PtDropdown, PtDropdownItem },
  data() {
    return {
      unpairedDrivers: [],
      unpairedVehicles: [],
      driverList: [],
      currentPairingDriver: null,
      currentPairingVehicle: null,
      currentUnpairingDriver: null,
      currentUnpairingVehicle: null,
      filteredUnpairedVehicle: [],
      pairingSearchKey: null,
      searchDriversWithVehicle: true,
      searchDriversWithoutVehicle: true,
      isLoadingDrivers: false,
      totalRows: 0,
      currentPage: PaginationConfig.CURRENT_PAGE,
      pageSize: PaginationConfig.PAGE_SIZE,
    };
  },
  computed: {
    selectedSearchDropDownText() {
      if (this.searchDriversWithVehicle && this.searchDriversWithoutVehicle) {
        return 'All';
      } else if (this.searchDriversWithVehicle) {
        return 'With Vehicle';
      }

      return 'Without Vehicle';
    },
    exportUrl() {
      let baseUrl = `${Config.api.baseURL}api/partners/driver-vehicles/list/export`;

      if (this.pairingSearchKey) {
        baseUrl = `${baseUrl}?searchBy=${this.pairingSearchKey}`;
      }

      if (this.selectedSearchDropDownText !== 'All') {
        const paramSign = this.pairingSearchKey ? '&' : '?';
        baseUrl = `${baseUrl}${paramSign}with-vehicle=${this.searchDriversWithVehicle}`;
      }

      return baseUrl;
    },
  },
  created() {
    this.debouncedSearchDriver = debounce(this.fetchDriverVehicles, 1000);
  },
  mounted() {
    this.fetchAllPairingData();
  },
  methods: {
    onPageChanged(page = PaginationConfig.CURRENT_PAGE, perPage = PaginationConfig.PAGE_SIZE) {
      this.currentPage = page;
      this.pageSize = perPage;
      this.fetchDriverVehicles();
    },
    setVehicleWithDriverSelector(withVehicle, withoutVehicle) {
      this.searchDriversWithVehicle = withVehicle;
      this.searchDriversWithoutVehicle = withoutVehicle;
      this.debouncedSearchDriver();
    },
    fetchAllPairingData() {
      this.fetchUnpairedDrivers();
      this.fetchUnpairedVehicles();
      this.fetchDriverVehicles();
    },
    fetchUnpairedDrivers() {
      makeRequest({
        url: `${Config.api.baseURL}api/partners/drivers-with-no-vehicle`,
      })
        .then((response) => {
          this.unpairedDrivers = response.data;
          this.unpairedDrivers = this.unpairedDrivers.map(item => {
            return {
              id: item.id,
              name: `${item.name} ( ${item.number})`,
            };
          });
        })
        .catch((e) => {
          console.error(e);
          this.$notification.error('Could not find unpaired vehicles');
        });
    },
    fetchUnpairedVehicles() {
      makeRequest({
        url: `${Config.api.baseURL}api/partners/vehicles-with-no-driver`,
      })
        .then((response) => {
          this.unpairedVehicles = response.data;
          this.filteredUnpairedVehicle = cloneArray(this.unpairedVehicles);
        })
        .catch((e) => {
          console.error(e);
          this.$notification.error('Could not find unpaired drivers');
        });
    },
    fetchDriverVehicles() {
      const params = {};

      params['perPage'] = this.pageSize;
      params['page'] = this.currentPage;

      if (this.pairingSearchKey) {
        params['searchBy'] = this.pairingSearchKey;
      }

      if (this.selectedSearchDropDownText !== 'All') {
        params['with-vehicle'] = this.searchDriversWithVehicle;
      }

      makeRequest({
        url: `${Config.api.baseURL}api/partners/driver-vehicles/list`,
        params,
      })
        .then((response) => {
          this.driverList = response.data.data;
          this.totalRows = response.data.meta.pagination.total;
          this.currentPage = response.data.meta.pagination.current_page;
          this.pageSize = response.data.meta.pagination.per_page;
        })
        .catch((e) => {
          console.error(e);
          this.$notification.error('Could not all drivers');
        });
    },
    updatePairing(driverId, vehicleId, isPairing = true) {
      if (!driverId) {
        this.$notification.error(`Could not ${isPairing ? 'pair' : 'unpair'} driver. Please select a valid driver`);
        return;
      }

      if (!vehicleId) {
        this.$notification.error(`Could not ${isPairing ? 'pair' : 'unpair'} vehicle. Please select a valid vehicle`);
        return;
      }

      const endPoint = isPairing ? 'pair' : 'unpair';

      makeRequest({
        method: 'POST',
        url: `${Config.api.baseURL}api/partners/drivers/${driverId}/vehicles/${vehicleId}/${endPoint}`,
      })
        .then((response) => {
          if (isPairing) {
            this.setPairingData();
            this.$refs.pairPrompt.closeModal();
          } else {
            this.setUnPairingData();
            this.$refs.unpairPrompt.closeModal();
          }
          this.$ga.event({
            eventCategory: 'Pairing',
            eventAction: 'Driver-Vehicle',
            eventLabel: `${isPairing ? 'Paired' : 'Unpaired'}`,
          });
          this.$notification.success(`Driver ${isPairing ? 'paired' : 'unpaired'} successfully`);
        })
        .catch((e) => {
          console.error(e);
          this.$notification.error(e.response.data);

          if (!isPairing) {
            this.shouldNotUnPair();
          } else {
            this.shouldNotPair();
          }
        })
        .finally(() => {
          this.fetchAllPairingData();
        });
    },
    onPairBtnClicked(driverId = null, vehicleId = null) {
      this.setPairingData(driverId, vehicleId);
      this.$refs.pairPrompt.showModal();
    },
    onUnpairBtnClicked(driverId = null, vehicleId = null) {
      this.setUnPairingData(driverId, vehicleId);
      this.$refs.unpairPrompt.showModal();
    },
    shouldNotPair() {
      this.setPairingData();
      this.$refs.pairPrompt.closeModal();
    },
    shouldPair() {
      this.updatePairing(this.currentPairingDriver, this.currentPairingVehicle);
      this.$refs.pairPrompt.closeModal();
    },
    shouldUnPair() {
      this.updatePairing(this.currentUnpairingDriver, this.currentUnpairingVehicle, false);
      this.$refs.unpairPrompt.closeModal();
    },
    shouldNotUnPair() {
      this.setUnPairingData();
      this.$refs.unpairPrompt.closeModal();
    },
    setPairingData(driverId = null, vehicleId = null) {
      this.currentPairingDriver = driverId;
      this.currentPairingVehicle = vehicleId;
    },
    setUnPairingData(driverId = null, vehicleId = null) {
      this.currentUnpairingDriver = driverId;
      this.currentUnpairingVehicle = vehicleId;
    },
    searchInUnpairedVehicles(searchKey) {
      let filtered;

      if (searchKey) {
        let lowerKey = searchKey.toLowerCase();
        lowerKey = lowerKey.trim();

        filtered = this.unpairedVehicles.filter((vehicle) => {
          const matchesModel = vehicle.car_models && vehicle.car_models.toLowerCase().indexOf(lowerKey) > -1;
          const matchesRegistration = vehicle.reg_number && vehicle.reg_number.toLowerCase().indexOf(lowerKey) > -1;

          return matchesModel || matchesRegistration;
        });
      } else {
        filtered = this.unpairedVehicles;
      }

      this.filteredUnpairedVehicle = cloneArray(filtered);
    },
  },
};
</script>
